import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, AlertCircle, Mail, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [accessToken, setAccessToken] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('failed');
        return;
      }

      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          if (data.access_token) setAccessToken(data.access_token);
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(30,41,59,0.8)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.1)',
        padding: '60px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        {status === 'checking' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(212,175,55,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <Loader size={40} style={{ color: '#D4AF37', animation: 'spin 1s linear infinite' }} />
            </div>
            <style>{`
              @keyframes spin { to { transform: rotate(360deg); } }
              @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
            `}</style>
            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '12px' }}>
              Processing Payment
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px' }}>
              Please wait while we confirm your purchase...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(52,211,153,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={40} style={{ color: '#34d399' }} />
            </div>
            <h2 style={{ color: 'white', fontSize: '28px', marginBottom: '12px' }}>
              Payment Successful!
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px', marginBottom: '32px', lineHeight: 1.6 }}>
              Thank you for your purchase! We've sent your access links to your email address.
            </p>
            
            <div style={{
              background: 'rgba(212,175,55,0.1)',
              border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <Mail size={24} style={{ color: '#D4AF37' }} />
                <span style={{ color: '#D4AF37', fontWeight: '700', fontSize: '18px' }}>
                  Check Your Email
                </span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: 1.6 }}>
                We've sent you an email with direct links to all 9 elements.
                Save that email - those links give you lifetime access!
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {accessToken && (
                <Link
                  to={`/books/cire-interactive?token=${accessToken}`}
                  data-testid="access-book-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                    borderRadius: '12px',
                    color: '#1e293b',
                    fontWeight: '700',
                    fontSize: '16px',
                    textDecoration: 'none',
                    boxShadow: '0 4px 20px rgba(212,175,55,0.3)'
                  }}
                >
                  Access Your Book Now
                  <ArrowRight size={18} />
                </Link>
              )}
              
              <Link
                to="/"
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                Return to Home
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(248,113,113,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <AlertCircle size={40} style={{ color: '#f87171' }} />
            </div>
            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '12px' }}>
              Payment Verification Failed
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px', marginBottom: '32px', lineHeight: 1.6 }}>
              We couldn't verify your payment. If you were charged, please contact support and we'll resolve this right away.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/books/cire-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: '600',
                  textDecoration: 'none'
                }}
              >
                Try Again
              </Link>
              
              <Link
                to="/contact"
                style={{
                  color: '#D4AF37',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                Contact Support
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
